import React from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  IconButton,
  Button,
  useTheme,
} from "@mui/material";
import {
  AddCircleOutline,
  DeleteOutline,
  Add,
  EventNote,
} from "@mui/icons-material";
import CustomSelect from "../../../../components/FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../../../components/FormFields/CustomInput";
import MultiSelect from "../../../../components/FormFields/CustomSelect/MultiSelect";
import CustomTextArea from "../../../../components/CustomForm/CustomTextArea";
import CustomDateTime from "../../../../components/CustomForm/CustomDateTime";
import CustomCheckbox from "../../../../components/CustomForm/CustomCheckbox";

const VisitDetailsForm = ({
  formData,
  handleChange,
  handleCheckBox,
  carePlanOptions,
  paymentServiceOptions,
  selectedCheckList,
  handleMultiSelectChange,
  formattedCheckList,
  handleAddInput,
  handleRemoveInput,
  handlePersonalizedCheck,
  isContinueIndifinite,
  setShowFundingModal,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "10px",
        border: "1px solid #E2E8F0",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          bgcolor: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        <EventNote sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
        <Typography variant="subtitle2" fontWeight={600} color="#1A1A2E">
          Visit Details
        </Typography>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <CustomInput
            label="Visit Name *"
            name="visit_name"
            value={formData.visit_name || ""}
            onChange={handleChange}
            required
            fullWidth
            size="small"
          />

          <CustomSelect
            options={carePlanOptions}
            label="Care Plan *"
            name="care_plan"
            value={formData.care_plan}
            onChange={handleChange}
            required
            fullWidth
            size="small"
            placeholder="Select Care Plan"
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 2 }}>
              <CustomSelect
                options={paymentServiceOptions}
                label="Payment Service *"
                name="payment_service"
                value={formData.payment_service}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                placeholder="Select Funding Profile"
              />
              <Button
                variant="text"
                size="small"
                startIcon={<AddCircleOutline sx={{ fontSize: 14 }} />}
                onClick={() => setShowFundingModal(true)}
                sx={{
                  mt: 0.5,
                  textTransform: "none",
                  fontSize: "0.7rem",
                  color: theme.palette.primary.main,
                  p: 0,
                  minHeight: 24,
                  "&:hover": { bgcolor: "transparent" },
                }}
              >
                Add Funding Profile
              </Button>
            </Box>
            <Box sx={{ flex: 1 }}>
              <CustomInput
                label="Carers *"
                name="carers_required"
                type="number"
                value={formData.carers_required || 1}
                onChange={handleChange}
                size="small"
              />
            </Box>
          </Box>

          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color="#475467"
              sx={{ mb: 0.5, display: "block" }}
            >
              Checklists
            </Typography>
            <MultiSelect
              options={formattedCheckList}
              value={selectedCheckList}
              onChange={handleMultiSelectChange}
              placeholder="Select checklists..."
            />
          </Box>

          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color="#475467"
              sx={{ mb: 0.5, display: "block" }}
            >
              Personalized Checks
            </Typography>
            <Stack spacing={0.5}>
              {formData.personalized_checks?.map((input, index) => (
                <Stack
                  key={input.id ?? `new-pc-${index}`}
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                >
                  <Box flexGrow={1}>
                    <CustomInput
                      placeholder="New check..."
                      value={input.check_name}
                      onChange={(e) => handlePersonalizedCheck(index, e)}
                      size="small"
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveInput(index)}
                    sx={{ color: "#EF4444", p: 0.5 }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button
              size="small"
              startIcon={<Add sx={{ fontSize: 14 }} />}
              onClick={handleAddInput}
              sx={{
                mt: 0.5,
                textTransform: "none",
                fontSize: "0.7rem",
                color: theme.palette.primary.main,
                p: 0,
                minHeight: 24,
                "&:hover": { bgcolor: "transparent" },
              }}
            >
              Add Check
            </Button>
          </Box>

          <CustomTextArea
            label="Instructions"
            name="support_instructions"
            value={formData.support_instructions || ""}
            onChange={handleChange}
            rows={5}
            size="small"
          />

          <Box
            sx={{
              display: "flex",
              gap: 2,
              p: 1.5,
              bgcolor: "#F8FAFC",
              borderRadius: "6px",
              border: "1px dashed #D0D5DD",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <CustomDateTime
                name="start_time"
                label="Start Date *"
                onChange={handleChange}
                value={formData.start_time || ""}
                required
                format="YYYY-MM-DD HH:mm"
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CustomCheckbox
                label="Indefinite"
                name="continue_indefinitely"
                checked={formData.continue_indefinitely || false}
                onClick={(c) => handleCheckBox("continue_indefinitely", c)}
              />
            </Box>
          </Box>

          {!isContinueIndifinite && (
            <CustomDateTime
              name="end_time"
              label="End Date"
              onChange={handleChange}
              value={formData.end_time || null}
              format="YYYY-MM-DD HH:mm"
            />
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

export default VisitDetailsForm;